import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useMutation } from "convex/react";
import {
  Article,
  Cards,
  Check,
  GearSix,
  Microphone,
  Pause,
  Play,
  PlugsConnected,
  Record,
  Scales,
  Sparkle,
  Stack,
  Stop,
  X,
} from "@phosphor-icons/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  DEFAULT_SESSION_STT_CONFIG,
  normalizeSessionSttConfig,
  type SessionSttProvider,
} from "../../shared/sttConfig.ts";
import type {
  RecapCoverageStatus,
  SuggestionCoverageStatus,
  TranscriptEntry,
} from "../../shared/types.ts";
import { api, type HealthResponse } from "../lib/api.ts";
import type { RoleAuth } from "../lib/auth.ts";
import {
  noNewQuestionsCopy,
  notificationCopy,
  suggestionsGeneratedCopy,
} from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import {
  activeElapsedBetween,
  activeTimeForTimestamp,
  inactiveIntervalsForCaptureClock,
  mapEntriesToActiveTime,
  selectCaptureClockEntries,
} from "../lib/activeIngestionTime.ts";
import { computeGenerationProgress } from "../lib/generationProgress.ts";
import {
  EPISODE_TARGET_SECONDS,
  EPISODE_TARGET_WORDS,
} from "../../shared/recaps/episodes.ts";
import {
  describeConsolidatedChip,
  episodeStatsLabel,
  recapCoverageChipState,
  suggestionCoverageChipState,
  transcriptChipState,
  windowChipState,
  windowStatsLabel,
} from "./functionChipState.ts";
import { SttSettingsPanel } from "./SttSettingsPanel.tsx";
import { ChipPopover } from "./ChipPopover.tsx";
import { FunctionChip } from "./FunctionChip.tsx";
import { LiveWaveform } from "./LiveWaveform.tsx";
import { useCaptureController } from "./useCaptureController.ts";
import type { OperatorShellState } from "./useOperatorState.ts";

/** Telemetry summary of the most recent suggestion generation run, sourced
 *  from `operatorRuntimeStatus.getForSession` via `useOperatorState`. */
export interface LastGenerationSummary {
  finishedAt: number;
  durationMs: number;
  candidateCount: number;
  keptCount: number;
  skipReason?: string;
}

type ChipId = "capture" | "transcript" | "suggestions" | "timeline" | "consolidated";

function freshness(ts: number | null, now: number): string {
  if (ts == null) return "—";
  const secs = Math.max(0, Math.round((now - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m ago`;
}

export function FunctionBar({
  auth,
  state,
  health,
  transcriptEntries,
  transcriptTotalEntries,
  aiThreadCount,
  recapCount,
  lastGenerationSummary,
  onInterim,
  onEnsureSessionActive,
}: {
  auth: RoleAuth;
  state: OperatorShellState;
  health: HealthResponse | null;
  transcriptEntries: TranscriptEntry[];
  /** Total persisted line count from the transcript summary — `transcriptEntries`
   *  is page-capped (100), so long sessions must not read it as the total. */
  transcriptTotalEntries: number | null;
  /** Count of AI-origin threads across all statuses (Suggestions chip stat). */
  aiThreadCount: number;
  /** Count of timeline recap entries on the client (Timeline chip stat). */
  recapCount: number;
  lastGenerationSummary: LastGenerationSummary | null;
  onInterim?: (t: string) => void;
  onEnsureSessionActive: (sessionId: string) => Promise<void> | void;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const [openId, setOpenId] = useState<ChipId | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Live level tapped straight from THIS tab's capture controller (~10Hz),
  // preferred over the throttled server-reported `status.micLevel`.
  const [localLevel, setLocalLevel] = useState<{ value: number; at: number } | null>(null);
  const [suggestionRecovery, setSuggestionRecovery] = useState<"retry" | "skip" | null>(null);
  const handleMicLevel = useCallback((value: number) => {
    setLocalLevel({ value, at: Date.now() });
  }, []);

  const setGenerationConfig = useMutation(convexApi.settings.setGenerationConfig);
  const setRecapConfig = useMutation(convexApi.settings.setRecapConfig);
  const saveSttConfig = useMutation(convexApi.settings.setSttConfig);
  const retryRepairableBlocked = useMutation(
    convexApi.suggestionCheckpointCoverage.retryRepairableBlockedCheckpointsAsOperator,
  );
  const skipBlocked = useMutation(
    convexApi.suggestionCheckpointCoverage.skipBlockedCheckpointsAsOperator,
  );

  const sessionId = state.meta.id;
  const status = state.status;
  const gen = state.generation;
  const recap = state.recap;

  // Ticking clock: the seconds/countdown values on the idle chips must advance
  // once a second. A single interval drives every chip; it runs only while some
  // countdown is on screen (see `anyCountdown`) and is cleared on unmount.
  const [now, setNow] = useState(() => Date.now());
  const inactiveIntervals = useMemo(
    () => inactiveIntervalsForCaptureClock(status),
    [
      status.captureState,
      status.captureStateChangedAt,
      status.captureClockStartedAt,
      status.captureClockInactiveIntervals,
    ],
  );
  const activeNow = activeTimeForTimestamp(now, inactiveIntervals);
  const progressTranscriptEntries = useMemo(
    () => selectCaptureClockEntries(transcriptEntries, status),
    [status.captureClockStartedAt, transcriptEntries],
  );
  const activeTranscriptEntries = useMemo(
    () => mapEntriesToActiveTime(progressTranscriptEntries, inactiveIntervals),
    [inactiveIntervals, progressTranscriptEntries],
  );
  const firstProgressEntryTs = progressTranscriptEntries.reduce<number | null>(
    (oldest, entry) => (oldest == null || entry.ts < oldest ? entry.ts : oldest),
    null,
  );
  const activeProcessedThroughTs = status.processedThroughTs == null ||
    (firstProgressEntryTs != null && status.processedThroughTs < firstProgressEntryTs)
    ? null
    : activeTimeForTimestamp(status.processedThroughTs, inactiveIntervals);
  const activeSuggestionCheckpointThroughTs = status.suggestionCheckpointThroughTs == null
    ? null
    : activeTimeForTimestamp(status.suggestionCheckpointThroughTs, inactiveIntervals);
  const coverage = status.recapCoverage ?? null;
  const suggestionCoverage = status.suggestionCoverage ?? null;
  const activeRecapCompletedThroughTs = coverage?.completedThroughTs == null ||
    (firstProgressEntryTs != null && coverage.completedThroughTs < firstProgressEntryTs)
    ? null
    : activeTimeForTimestamp(coverage.completedThroughTs, inactiveIntervals);

  const progress = computeGenerationProgress({
    entries: activeTranscriptEntries,
    thresholdWords: gen.thresholdWords,
    thresholdSeconds: gen.thresholdSeconds,
    // Durable checkpoint coverage supersedes the legacy combined Node-pass marker.
    processedThroughTs: suggestionCoverage ? null : activeProcessedThroughTs,
    checkpointThroughTs: activeSuggestionCheckpointThroughTs,
    now: activeNow,
  });
  const recapProgress = computeGenerationProgress({
    entries: activeTranscriptEntries,
    thresholdWords: EPISODE_TARGET_WORDS,
    thresholdSeconds: EPISODE_TARGET_SECONDS,
    processedThroughTs: activeRecapCompletedThroughTs,
    now: activeNow,
  });

  const consolidation = status.consolidation ?? null;

  // Durable checkpoint state owns Suggestions progress after cutover. Legacy
  // runtime fields remain only as a compatibility fallback for unmigrated sessions.
  const suggestionsPhase = suggestionCoverage &&
      (suggestionCoverage.phase === "generating" || suggestionCoverage.phase === "judging")
    ? suggestionCoverage.phase
    : status.generationPhase ?? null;
  const suggestionsBusy = suggestionCoverage
    ? suggestionCoverage.phase === "generating" || suggestionCoverage.phase === "judging"
    : suggestionsPhase != null || status.generating;
  const timelineBusy = coverage
    ? ["generating", "verifying", "finalizing"].includes(coverage.phase)
    : status.liveRecapGenerating;
  const consolidationBusy = status.consolidationGenerating;

  // Countdowns are on screen whenever a chip sits in its idle-progress state
  // (i.e. not mid-run). Keep the ticking interval alive only then.
  const timelineAccruing = coverage == null || coverage.phase === "accruing";
  const anyCountdown = !suggestionsBusy || timelineAccruing || (consolidation != null && !consolidationBusy);
  useEffect(() => {
    if (!anyCountdown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anyCountdown]);

  // Runtime `lastTranscriptAt` only reflects live capture in this browser
  // session; fall back to the newest persisted entry so a loaded transcript
  // never reads "—" (parity with the V1 statusbar's transcript summary).
  const newestEntryTs = transcriptEntries.reduce<number | null>(
    (newest, entry) => (newest == null || entry.ts > newest ? entry.ts : newest),
    null,
  );
  const lastTranscriptAt = status.lastTranscriptAt ?? newestEntryTs;

  const providerKeys = health?.runtime.sttProviderKeys ?? {
    deepgram: false,
    speechmatics: false,
    soniox: false,
  };
  const ingressSharedSecretConfigured = health?.runtime.ingressSharedSecretConfigured ?? false;
  const capture = useCaptureController({
    sessionId,
    sessionStatus: state.meta.status,
    onInterim: onInterim ?? (() => {}),
    onLevel: handleMicLevel,
    auth,
    stt: state.stt,
    providerKeys,
    ingressSharedSecretConfigured,
    onEnsureSessionActive,
  });

  useEffect(() => {
    if (!openId) return;
    function onDocMouseDown(event: MouseEvent) {
      if (barRef.current && !barRef.current.contains(event.target as Node)) close();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openId]);

  function close() {
    setOpenId(null);
    setAnchorEl(null);
  }

  function toggle(id: ChipId, el: HTMLElement) {
    setOpenId((current) => {
      if (current === id) {
        setAnchorEl(null);
        return null;
      }
      setAnchorEl(el);
      return id;
    });
  }

  async function saveGeneration(patch: {
    thresholdSeconds?: number;
    thresholdWords?: number;
    judgeEnabled?: boolean;
    aiDebugEnabled?: boolean;
  }, options: { notifySuccess?: boolean; rethrow?: boolean } = {}) {
    try {
      await setGenerationConfig({
        auth,
        sessionId: sessionId as Id<"sessions">,
        purpose: gen.purpose,
        themes: gen.themes,
        thresholdSeconds: patch.thresholdSeconds ?? gen.thresholdSeconds,
        thresholdWords: patch.thresholdWords ?? gen.thresholdWords,
        judgeEnabled: patch.judgeEnabled ?? gen.judgeEnabled,
        aiDebugEnabled: patch.aiDebugEnabled ?? gen.aiDebugEnabled,
      });
      if (options.notifySuccess) notify.success(notificationCopy.admin.contextSaved);
    } catch (error) {
      notify.error(notificationCopy.admin.couldNotSaveContext);
      if (options.rethrow) throw error;
    }
  }

  async function saveRecap(
    patch: { enabled?: boolean; consolidationSpacingMs?: number },
    options: { notifySuccess?: boolean; rethrow?: boolean } = {},
  ) {
    try {
      await setRecapConfig({
        auth,
        sessionId: sessionId as Id<"sessions">,
        enabled: patch.enabled ?? recap.enabled,
        ...(patch.consolidationSpacingMs !== undefined
          ? { consolidationSpacingMs: patch.consolidationSpacingMs }
          : {}),
      });
      if (options.notifySuccess) notify.success(notificationCopy.admin.summarySettingsSaved);
    } catch (error) {
      notify.error(notificationCopy.admin.couldNotSaveSummarySettings);
      if (options.rethrow) throw error;
    }
  }

  async function generateSuggestionsNow() {
    try {
      const result = await api.generateNow({
        force: true,
        kind: "suggestions",
      });
      if (result.created.length > 0) {
        notify.success(suggestionsGeneratedCopy({
          createdCount: result.created.length,
          candidateCount: result.candidateCount,
          keptCount: result.keptCount,
        }));
      } else if (isEmptySuggestionRun(result.reason)) {
        notify.info(noNewQuestionsCopy(result.reason));
      } else {
        notify.error(notificationCopy.admin.couldNotGenerateQuestions);
      }
    } catch {
      notify.error(notificationCopy.admin.couldNotGenerateQuestions);
    }
  }

  async function retryBlockedSuggestions() {
    setSuggestionRecovery("retry");
    try {
      const result = await retryRepairableBlocked({
        auth,
        sessionId: sessionId as Id<"sessions">,
      });
      if (result.retriedCount > 0) {
        notify.success(notificationCopy.admin.blockedSuggestionsRetried, {
          description: `${result.retriedCount} checkpoint${result.retriedCount === 1 ? "" : "s"} queued.`,
        });
      } else {
        notify.info("No automatically repairable checkpoints remain.");
      }
    } catch {
      notify.error(notificationCopy.admin.couldNotRecoverSuggestions);
    } finally {
      setSuggestionRecovery(null);
    }
  }

  async function skipBlockedSuggestions() {
    const blockedCount = suggestionCoverage?.blockedCheckpointCount ?? 0;
    if (blockedCount === 0) return;
    if (!window.confirm(
      `Resolve ${blockedCount} blocked suggestion moment${blockedCount === 1 ? "" : "s"} without generating questions?`,
    )) return;
    setSuggestionRecovery("skip");
    try {
      const result = await skipBlocked({
        auth,
        sessionId: sessionId as Id<"sessions">,
        reason: "Operator resolved the stale blocked moment without generating a question.",
      });
      notify.success(notificationCopy.admin.blockedSuggestionsSkipped, {
        description: `${result.skippedCount} checkpoint${result.skippedCount === 1 ? "" : "s"} covered as skipped.`,
      });
    } catch {
      notify.error(notificationCopy.admin.couldNotRecoverSuggestions);
    } finally {
      setSuggestionRecovery(null);
    }
  }

  async function saveSttProvider(provider: SessionSttProvider) {
    const config = normalizeSessionSttConfig({
      ...state.stt,
      mode: "live",
      provider,
    }, DEFAULT_SESSION_STT_CONFIG);
    try {
      await saveSttConfig({ auth, sessionId: sessionId as Id<"sessions">, config });
    } catch {
      notify.error(notificationCopy.admin.couldNotSaveContext);
    }
  }

  const captureActive = capture.active || status.micActive;
  const capturePaused = capture.paused || status.micPaused;
  const captureLabel = captureActive ? (capturePaused ? "paused" : "active") : "off";
  const micDotClass = captureActive ? (capturePaused ? "paused" : "live") : "off";
  const transportOk = status.audioTransport === "connected";
  const micName = capture.snapshot.selectedInputDeviceLabel ?? status.selectedInputDeviceLabel;

  // Prefer the in-tab live level while it is fresh (capture running here); fall
  // back to the server-reported level when capture is driven elsewhere.
  const localLevelFresh = localLevel != null && Date.now() - localLevel.at < 600;
  const vuLevel = localLevelFresh ? localLevel.value : (capture.snapshot.micLevel || status.micLevel);

  const transcriptLines = transcriptTotalEntries ?? transcriptEntries.length;

  // State pills: process state is separated from stats on every chip. The
  // pill sits at the value row's right edge; stats stay left and numeric.
  const transcriptState = transcriptChipState({
    sttError: Boolean(status.sttError),
    captureActive,
    capturePaused,
  });
  const suggestionsState = suggestionCoverage
    ? suggestionCoverageChipState(suggestionCoverage)
    : windowChipState({
        busy: suggestionsBusy,
        phase: suggestionsPhase,
        stale: progress.stale,
      });
  const suggestionsStats = suggestionCoverage && suggestionCoverage.phase !== "accruing"
    ? suggestionCoverageStats(suggestionCoverage)
    : windowStatsLabel({
        busy: suggestionsBusy,
        stale: suggestionCoverage?.accruingStale ?? progress.stale,
        words: suggestionCoverage?.accruingWordCount ?? progress.words,
        thresholdWords: gen.thresholdWords,
        seconds: suggestionCoverage
          ? Math.round(suggestionCoverage.accruingDurationMs / 1000)
          : progress.seconds,
        thresholdSeconds: gen.thresholdSeconds,
        secondsFormat: "plain",
      });
  const timelineState = coverage
    ? recapCoverageChipState(coverage, recapProgress.stale)
    : windowChipState({ busy: timelineBusy, stale: recapProgress.stale });
  const timelineStats = coverage && coverage.phase !== "accruing"
    ? coverageWindowStats(coverage)
    : episodeStatsLabel({
        busy: timelineBusy,
        stale: recapProgress.stale,
        words: recapProgress.words,
        seconds: recapProgress.seconds,
      });
  const suggestionsMeter = suggestionCoverage && suggestionCoverage.phase !== "accruing"
    ? !suggestionCoverage.countsClamped && suggestionCoverage.assignedCheckpointCount > 0
      ? (suggestionCoverage.terminalCheckpointCount /
          suggestionCoverage.assignedCheckpointCount) * 100
      : null
    : suggestionsBusy
    ? null
    : gen.thresholdWords > 0
      ? ((suggestionCoverage?.accruingWordCount ?? progress.words) / gen.thresholdWords) * 100
      : 0;
  const timelineMeter = coverage && coverage.phase !== "accruing"
    ? coverage.discoveredWindowCount > 0
      ? (coverage.completedWindowCount / coverage.discoveredWindowCount) * 100
      : null
    : timelineBusy
      ? null
      : EPISODE_TARGET_WORDS > 0
        ? Math.max(
            recapProgress.words / EPISODE_TARGET_WORDS,
            recapProgress.seconds / EPISODE_TARGET_SECONDS,
          ) * 100
        : 0;
  const consolidatedElapsedMs = consolidation?.lastConsolidatedAt == null
    ? null
    : activeElapsedBetween(consolidation.lastConsolidatedAt, now, inactiveIntervals);
  const consolidatedChip = describeConsolidatedChip(
    consolidation
      ? { ...consolidation, spacingMs: recap.consolidationSpacingMs }
      : consolidation,
    consolidatedElapsedMs,
    consolidationBusy,
    status.captureState,
    now,
  );

  return (
    <div
      className="admin2-function-bar pipeline"
      data-testid="admin2-function-bar"
      ref={barRef}
    >
      <div className="cluster">
        <div className="stage stage-capture capture-inline" data-testid="chip-capture" data-chip="capture">
          <div className="stage-top">
            <Microphone className="ic i-mut" />
            <span className="nm">Capture</span>
            <span className="mic-name">· {captureLabel}</span>
            <span className={`chip-dot ${micDotClass}`} data-testid="status-mic" title={`Mic ${captureLabel}`} />
            <span
              className={`chip-dot ${transportOk ? "live" : "off"}`}
              data-testid="status-transport"
              title={`Audio transport ${status.audioTransport}`}
            />
          </div>
          <div className="capture-inline-row">
            <select
              className="capture-provider-select"
              data-testid="function-bar-provider-select"
              value={state.stt.provider}
              disabled={captureActive}
              title={captureActive ? "Provider changes apply on next start" : "Transcription provider"}
              onChange={(event) => void saveSttProvider(event.target.value as SessionSttProvider)}
            >
              <option value="deepgram">Deepgram</option>
              <option value="speechmatics">Speechmatics</option>
              <option value="soniox">Soniox</option>
            </select>
            <select
              className="capture-device-select"
              data-testid="function-bar-mic-device-select"
              value={capture.selectedDeviceId ?? ""}
              disabled={captureActive || capture.loadingDevices}
              title={captureActive ? "Mic changes apply on next start" : "Audio input device"}
              onChange={(event) => capture.setSelectedDeviceId(event.target.value || null)}
            >
              {capture.devices.length === 0 ? (
                <option value="">{micName ?? "No input"}</option>
              ) : (
                capture.devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              className="capture-icon-btn"
              data-testid="function-bar-refresh-devices"
              title={capture.deviceButtonLabel}
              aria-label={capture.deviceButtonLabel}
              disabled={captureActive || capture.loadingDevices}
              onClick={() => void capture.refreshDevices(true)}
            >
              <PlugsConnected />
            </button>
            <div className="capture-wave">
              <LiveWaveform active={captureActive && !capturePaused} level={vuLevel} />
            </div>
            <div className="capture-transport" role="group" aria-label="Capture transport">
              {!captureActive ? (
                <button
                  type="button"
                  className="capture-icon-btn is-primary"
                  data-testid="mic-start"
                  title="Start capture"
                  disabled={capture.starting}
                  onClick={() => void capture.start()}
                >
                  <Record weight="fill" />
                </button>
              ) : capturePaused ? (
                <>
                  <button
                    type="button"
                    className="capture-icon-btn is-primary"
                    data-testid="mic-resume"
                    title="Resume capture"
                    onClick={() => void capture.resume()}
                  >
                    <Play weight="fill" />
                  </button>
                  <button
                    type="button"
                    className="capture-icon-btn is-danger"
                    data-testid="mic-stop"
                    title="Stop capture"
                    onClick={() => void capture.stop()}
                  >
                    <Stop weight="fill" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="capture-icon-btn"
                    data-testid="mic-pause"
                    title="Pause capture"
                    onClick={() => capture.pause()}
                  >
                    <Pause weight="fill" />
                  </button>
                  <button
                    type="button"
                    className="capture-icon-btn is-danger"
                    data-testid="mic-stop"
                    title="Stop capture"
                    onClick={() => void capture.stop()}
                  >
                    <Stop weight="fill" />
                  </button>
                </>
              )}
            </div>
            <button
              type="button"
              className="capture-icon-btn"
              data-testid="function-bar-capture-settings"
              title="Capture settings"
              onClick={(event) => toggle("capture", event.currentTarget)}
            >
              <GearSix />
            </button>
          </div>
          {(capture.error || capture.snapshot.audioTransportError) && (
            <div className="capture-inline-error" data-testid="mic-error">
              {capture.error ?? capture.snapshot.audioTransportError}
            </div>
          )}
        </div>

        <FunctionChip
          id="transcript"
          className="stage-transcript"
          icon={<Article className="ic i-mut" />}
          name="Transcript"
          title="Transcript freshness"
          value={<>{transcriptLines} line{transcriptLines === 1 ? "" : "s"}</>}
          state={transcriptState}
          stateTestid="chip-state-transcript"
          onOpen={(el) => toggle("transcript", el)}
        />

        <FunctionChip
          id="suggestions"
          className="stage-suggestions"
          icon={<Sparkle weight="fill" className="ic i-ai" />}
          name="Suggestions"
          suffix={
            <>
              <span className="chip-stat">· {aiThreadCount} generated</span>
              {(suggestionCoverage?.blockedCheckpointCount ?? 0) > 0 && (
                <span className="chip-stat attention">
                  · {suggestionCoverage!.blockedCheckpointCount} review
                </span>
              )}
            </>
          }
          title="Click to tune thresholds"
          value={suggestionsStats}
          meter={suggestionsMeter}
          state={suggestionsState}
          alert={suggestionCoverage?.structuralBlocked === true}
          stateTestid="chip-state-suggestions"
          onOpen={(el) => toggle("suggestions", el)}
        />

        <FunctionChip
          id="timeline"
          className="stage-timeline"
          icon={<Cards className="ic i-mut" />}
          name="Timeline"
          suffix={<span className="chip-stat">· {recapCount} entr{recapCount === 1 ? "y" : "ies"}</span>}
          title="Click to tune the recap window"
          value={timelineStats}
          meter={timelineMeter}
          state={timelineState}
          alert={coverage?.phase === "blocked"}
          stateTestid="chip-state-timeline"
          onOpen={(el) => toggle("timeline", el)}
        />

        <FunctionChip
          id="consolidated"
          className="stage-consolidated"
          icon={<Stack className="ic i-mut" />}
          name="Consolidated"
          suffix={consolidation ? <span className="chip-stat">· {consolidation.inputSectionCount} merged</span> : null}
          alert={consolidatedChip.alert}
          title="Merge the timeline into one summary"
          value={<span data-testid="status-consolidation">{consolidatedChip.stats}</span>}
          state={consolidatedChip.state}
          stateTestid="chip-state-consolidated"
          onOpen={(el) => toggle("consolidated", el)}
        />
      </div>

      <ChipPopover
        id="capture"
        hidden={openId !== "capture"}
        anchor={openId === "capture" ? anchorEl : null}
        container={barRef.current}
        onClose={close}
        title={
          <>
            <GearSix className="i-mut" /> Capture settings
          </>
        }
      >
        <div className="pop-sub">Advanced transcription settings and provider readiness.</div>
        <SttSettingsPanel
          bare
          hideProviderSelect
          auth={auth}
          sessionId={sessionId}
          stt={state.stt}
          providerKeys={providerKeys}
          ingressSharedSecretConfigured={ingressSharedSecretConfigured}
          captureActive={captureActive}
        />
      </ChipPopover>

      {openId === "transcript" && (
        <ChipPopover
          id="transcript"
          anchor={anchorEl}
          container={barRef.current}
          onClose={close}
          title={
            <>
              <Article className="i-mut" /> Transcript
            </>
          }
        >
          <div className="pop-sub">
            {transcriptLines} lines · newest{" "}
            <span data-testid="transcript-last-updated">{freshness(lastTranscriptAt, now)}</span>.
          </div>
          <div className="pop-last">Edit lines &amp; fix diarization in the transcript rail on the right.</div>
        </ChipPopover>
      )}

      {openId === "suggestions" && (
        <ChipPopover
          id="suggestions"
          anchor={anchorEl}
          container={barRef.current}
          onClose={close}
          title={
            <>
              <Sparkle weight="fill" className="i-ai" /> Suggestions
            </>
          }
        >
          <div className="pop-sub">Generates when BOTH thresholds are met on the current answer block.</div>
          {(suggestionCoverage?.blockedCheckpointCount ?? 0) > 0 && (
            <section className="suggestion-history-attention" aria-label="Blocked suggestion history">
              <strong>
                {suggestionCoverage!.blockedCheckpointCount} earlier moment{
                  suggestionCoverage!.blockedCheckpointCount === 1 ? "" : "s"
                } need review
              </strong>
              <p>
                Generation is still advancing. Retry failures fixed by this release, or resolve stale moments without adding questions.
              </p>
              {suggestionCoverage?.errorSummary && (
                <small title={suggestionCoverage.errorSummary}>{suggestionCoverage.errorSummary}</small>
              )}
              <div className="suggestion-history-actions">
                <button
                  type="button"
                  className="btn ghost sm"
                  data-testid="retry-blocked-suggestions"
                  disabled={suggestionRecovery !== null ||
                    (suggestionCoverage?.repairableBlockedCheckpointCount ?? 0) === 0}
                  onClick={() => void retryBlockedSuggestions()}
                >
                  {suggestionRecovery === "retry" ? "Retrying…" : "Retry repairable"}
                </button>
                <button
                  type="button"
                  className="btn ghost sm danger"
                  data-testid="skip-blocked-suggestions"
                  disabled={suggestionRecovery !== null}
                  onClick={() => void skipBlockedSuggestions()}
                >
                  {suggestionRecovery === "skip" ? "Resolving…" : "Resolve as skipped"}
                </button>
              </div>
            </section>
          )}
          <ThresholdEditor
            secondsValue={gen.thresholdSeconds}
            wordsValue={gen.thresholdWords}
            secondsTestId="generation-threshold-seconds"
            wordsTestId="generation-threshold-words"
            actionTestIdPrefix="generation"
            onSave={({ seconds, words }) => saveGeneration(
              { thresholdSeconds: seconds, thresholdWords: words },
              { notifySuccess: true, rethrow: true },
            )}
          />
          <label className="pop-toggle">
            <span>
              <Scales className="i-mut" /> Run Groq judge
            </span>
            <input
              type="checkbox"
              data-testid="generation-judge-enabled"
              checked={gen.judgeEnabled}
              onChange={(event) => void saveGeneration({ judgeEnabled: event.target.checked })}
            />
          </label>
          <p className="pop-toggle-help" data-testid="generation-judge-explanation">
            {gen.judgeEnabled
              ? "On: generate up to 3 candidates, then run a separate LLM judge."
              : "Off: explicitly request zero or 1 suggestion; publish it only if validation passes."}
          </p>
          <div className="pop-last">
            now — {suggestionCoverage?.accruingWordCount ?? progress.words}w · {
              (suggestionCoverage?.accruingStale ?? progress.stale)
                ? "waiting"
                : `${suggestionCoverage
                    ? Math.round(suggestionCoverage.accruingDurationMs / 1000)
                    : progress.seconds}s`
            }
            <br />
            last — {suggestionCoverage
              ? describeDurableSuggestionRun(suggestionCoverage)
              : describeLastRun(lastGenerationSummary, now)}
          </div>
          <div className="pop-foot">
            <button
              type="button"
              className="btn primary sm"
              style={{ flex: 1 }}
              data-testid="generate-now"
              title="Generate questions now"
              onClick={() => void generateSuggestionsNow()}
            >
              Generate questions now
            </button>
          </div>
        </ChipPopover>
      )}

      {openId === "timeline" && (
        <ChipPopover
          id="timeline"
          anchor={anchorEl}
          container={barRef.current}
          onClose={close}
          title={
            <>
              <Cards className="i-mut" /> Timeline
            </>
          }
        >
          <div className="pop-sub" data-testid="timeline-coverage-summary">
            {coverage
              ? describeTimelineCoverage(coverage)
              : "Builds one live timeline artifact from each semantic episode."}
          </div>
          <label className="pop-toggle">
            <span>Generate interim summaries</span>
            <input
              type="checkbox"
              data-testid="recap-enabled"
              checked={recap.enabled}
              onChange={(event) => void saveRecap({ enabled: event.target.checked })}
            />
          </label>
          <label className="pop-toggle">
            <span>Key-themes refresh spacing</span>
            <select
              data-testid="recap-spacing"
              value={String(recap.consolidationSpacingMs ?? 60_000)}
              onChange={(event) =>
                void saveRecap(
                  { consolidationSpacingMs: Number(event.target.value) },
                  { notifySuccess: true },
                )}
            >
              <option value="60000">60s — fast</option>
              <option value="90000">90s — relaxed</option>
            </select>
          </label>
          <div className="pop-last">
            {coverage?.phase === "accruing" || coverage == null
              ? <>now — {recapProgress.words}w · {fmtEpisodeProgress(recapProgress.seconds, recapProgress.stale)}</>
              : <>coverage — {coverageWindowStats(coverage)}</>}
            {coverage?.integrityCodes.length ? (
              <><br />attention — {coverage.integrityCodes.join(", ")}</>
            ) : null}
            <br />
            review and publish timeline summaries from Summaries.
          </div>
        </ChipPopover>
      )}

      {openId === "consolidated" && (
        <ChipPopover
          id="consolidated"
          anchor={anchorEl}
          container={barRef.current}
          onClose={close}
          title={
            <>
              <Stack className="i-mut" /> Consolidated
            </>
          }
        >
          <div className="pop-sub">Merges the timeline into one summary on a cadence.</div>
          <div className="pop-last" style={{ borderTop: 0, paddingTop: 0 }}>
            last — {describeConsolidation(consolidation, now)}
            <br />
            generate and publish from Summaries.
          </div>
        </ChipPopover>
      )}
    </div>
  );
}

function coverageWindowStats(coverage: RecapCoverageStatus): string {
  const suffix = coverage.countsClamped ? "+" : "";
  return `${coverage.completedWindowCount}/${coverage.discoveredWindowCount} covered${suffix}`;
}

function suggestionCoverageStats(coverage: SuggestionCoverageStatus): string {
  const suffix = coverage.countsClamped ? "+" : "";
  switch (coverage.phase) {
    case "blocked": return `${coverage.blockedCheckpointCount}${suffix} blocked`;
    case "retrying": return `${coverage.retryableCheckpointCount}${suffix} retrying`;
    case "generating":
    case "judging": return `${coverage.pendingCheckpointCount}${suffix} open`;
    case "caught_up":
      return `${coverage.terminalCheckpointCount}/${coverage.assignedCheckpointCount} covered`;
    case "accruing": return "";
  }
}

function describeDurableSuggestionRun(coverage: SuggestionCoverageStatus): string {
  if (coverage.lastSuggestionLatencyMs == null) return "—";
  return `${(coverage.lastSuggestionLatencyMs / 1000).toFixed(1)}s provider + judge`;
}

function describeTimelineCoverage(coverage: RecapCoverageStatus): string {
  const backlog = coverage.pendingWindowCount +
    coverage.retryableWindowCount +
    coverage.verificationWindowCount +
    coverage.blockedWindowCount;
  const backlogLabel = coverage.countsClamped ? `${backlog}+` : String(backlog);
  switch (coverage.phase) {
    case "accruing": return "Accumulating the next content-based timeline window.";
    case "generating": return `Generating durable timeline coverage · ${backlogLabel} open.`;
    case "retrying": return `A provider or lease attempt is retrying · ${backlogLabel} open.`;
    case "verifying": return `Verifying ${coverage.verificationWindowCount}${coverage.countsClamped ? "+" : ""} corrected window(s).`;
    case "blocked": return coverage.errorSummary ?? "Timeline coverage is blocked by an integrity failure.";
    case "caught_up": return "Every discovered content window is covered.";
    case "finalizing": return `Final session coverage is ${coverage.finalizationStatus ?? "in progress"}.`;
  }
}

function describeLastRun(summary: LastGenerationSummary | null, now: number): string {
  if (!summary) return "—";
  if (summary.skipReason) return `skipped: ${summary.skipReason}`;
  const secs = (summary.durationMs / 1000).toFixed(1);
  return `kept ${summary.keptCount} of ${summary.candidateCount} candidates · ${secs}s · ${freshness(summary.finishedAt, now)}`;
}

function isEmptySuggestionRun(reason: string | undefined): boolean {
  return reason === "no_checkpoint" ||
    reason === "no_pending_source" ||
    reason === "empty" ||
    reason === "vetoed" ||
    reason === "duplicate" ||
    reason === "duplicates" ||
    reason === "scheduled" ||
    reason === "retrying" ||
    reason === "all_candidates_filtered" ||
    reason === "already_generating";
}

/** Consolidation cadence summary for the Consolidated chip/popover. `consolidation`
 *  is null while `sessions.getOperatorShellState` is still loading. */
function describeConsolidation(
  consolidation: OperatorShellState["status"]["consolidation"],
  now: number,
): string {
  if (!consolidation) return "—";
  const pendingCount = consolidation.pendingEpisodeCount >= 100
    ? "99+"
    : String(consolidation.pendingEpisodeCount);
  return `${pendingCount} pending · last ${freshness(consolidation.lastConsolidatedAt, now)}`;
}

function fmtEpisodeProgress(seconds: number, stale: boolean): string {
  if (stale) return "waiting";
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")} live episode`;
}

function ThresholdEditor({
  secondsValue,
  wordsValue,
  secondsLabel = "Seconds",
  wordsLabel = "Words",
  secondsTestId,
  wordsTestId,
  actionTestIdPrefix,
  onSave,
}: {
  secondsValue: number;
  wordsValue: number;
  secondsLabel?: string;
  wordsLabel?: string;
  secondsTestId: string;
  wordsTestId: string;
  actionTestIdPrefix: string;
  onSave: (values: { seconds: number; words: number }) => Promise<void>;
}) {
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [baseline, setBaseline] = useState({ seconds: secondsValue, words: wordsValue });
  const [seconds, setSeconds] = useState(String(secondsValue));
  const [words, setWords] = useState(String(wordsValue));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");

  useEffect(() => {
    setBaseline({ seconds: secondsValue, words: wordsValue });
    setSeconds(String(secondsValue));
    setWords(String(wordsValue));
    setStatus("idle");
  }, [secondsValue, wordsValue]);

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const parsedSeconds = parsePositiveInteger(seconds);
  const parsedWords = parsePositiveInteger(words);
  const valid = parsedSeconds != null && parsedWords != null;
  const dirty = seconds !== String(baseline.seconds) || words !== String(baseline.words);

  async function save() {
    if (!dirty || saving || parsedSeconds == null || parsedWords == null) return;
    setSaving(true);
    setStatus("idle");
    try {
      await onSave({ seconds: parsedSeconds, words: parsedWords });
      setBaseline({ seconds: parsedSeconds, words: parsedWords });
      setSeconds(String(parsedSeconds));
      setWords(String(parsedWords));
      setStatus("saved");
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setStatus("idle"), 1600);
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    if (saving) return;
    setSeconds(String(baseline.seconds));
    setWords(String(baseline.words));
    setStatus("idle");
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      void save();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancel();
    }
  }

  return (
    <div className="thr-editor" onKeyDown={onKeyDown}>
      <div className="thr-row">
        <div className="thr-field">
          <label htmlFor={secondsTestId}>{secondsLabel}</label>
          <input
            id={secondsTestId}
            type="number"
            min={1}
            step={1}
            data-testid={secondsTestId}
            value={seconds}
            aria-invalid={!valid}
            onChange={(event) => {
              setSeconds(event.target.value);
              setStatus("idle");
            }}
          />
        </div>
        <div className="thr-field">
          <label htmlFor={wordsTestId}>{wordsLabel}</label>
          <input
            id={wordsTestId}
            type="number"
            min={1}
            step={1}
            data-testid={wordsTestId}
            value={words}
            aria-invalid={!valid}
            onChange={(event) => {
              setWords(event.target.value);
              setStatus("idle");
            }}
          />
        </div>
        <div className="thr-actions" aria-label="Threshold edit actions">
          <button
            type="button"
            className="thr-action save"
            data-testid={`${actionTestIdPrefix}-threshold-save`}
            aria-label="Save threshold changes"
            title="Save threshold changes"
            disabled={!dirty || !valid || saving}
            onClick={() => void save()}
          >
            <Check weight="bold" />
          </button>
          <button
            type="button"
            className="thr-action cancel"
            data-testid={`${actionTestIdPrefix}-threshold-cancel`}
            aria-label="Cancel threshold changes"
            title="Cancel threshold changes"
            disabled={!dirty || saving}
            onClick={cancel}
          >
            <X weight="bold" />
          </button>
        </div>
      </div>
      <div className={`thr-status ${status}`} data-testid={`${actionTestIdPrefix}-threshold-status`} aria-live="polite">
        {!valid
          ? "Enter positive whole numbers."
          : saving
            ? "Saving..."
            : status === "saved"
              ? "Saved."
              : status === "error"
                ? "Could not save."
                : dirty
                  ? "Press Enter or click the check to save."
                  : "Saved values are active."}
      </div>
    </div>
  );
}

function parsePositiveInteger(value: string): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed;
}
